import { LoaderBar } from '@components/LoaderBar';
import { useEffect, useMemo, useState } from 'react';
import PurchaseOrdersService from '@utility/services/purchase-order';
import { ToastMessage } from '@utility/toast';
import { ErrorMessageConstant } from '@utility/constants/common';
import { AppTable } from '@components/AppTable';
import {
  MultiAliasItemRow,
  createPOItemColumns,
  PredicateSelectAll,
  PredicateDeSelectAll,
  calculateItem,
  poQtyChanges,
  poUpdatePredictedQtyForItem,
  poUpdateAllPredicate,
  updateAttribute,
  addAttribute,
  EditItemPricesPopup,
  POTableSearch,
  deleteAttribute,
} from '@page-components/purchase-order';
import { CondensedTimelinePopup } from '@page-components/purchase-order/CondensedTimeline';
import { Button } from '@skupreme/shared/ui';
import { Minus, Plus } from '@phosphor-icons/react';
import { convertItemToMetric } from '@page-components/shipment';
import { DeleteConfirmPopup } from '@components/DeleteConfirm';
import { AddEditAttributePopup } from '@page-components/attribute-popup';
import { AppModal } from '@elements/Modal';
import { purchaseOrderItemsHook } from '@hooks/purchase-order-items';

export function AddItemsPopup({ closeModal, open, poData }) {
  const [imperial, setImperial] = useState();
  const [showAllItems, setShowAllItems] = useState(false);

  const [saving, setSaving] = useState(false);
  const [upcQty, setUPCQty] = useState({});
  const [currentItem, setCurrentItem] = useState({});
  const [currentTimelineItem, setCurrentTimelineItem] = useState();
  const [regionId, setRegionId] = useState(null);
  const [supplierId, setSupplierId] = useState(null);
  const [confirmationPopup, setConfirmationPopup] = useState(false);

  const [attributePopup, setAttributePopup] = useState(false);
  const [currentAttributeAlias, setCurrentAttributeAlias] = useState('');

  // Price Popup
  const [editPricePopup, setEditPricePopup] = useState(false);

  const {
    data: poItems,
    loading: itemLoading,
    pageChanged,
    totalPages,
    setData,
    filter,
    totalData,
    refresh,
    search,
    setSearch,
    filterChanged,
    // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'string'.
  } = purchaseOrderItemsHook(supplierId, imperial, upcQty, true);

  const items = useMemo(() => {
    if (poData._id) {
      return (poItems || []).filter(
        x => !(poData.items || []).some(y => y.item_upc === x.item_upc),
      );
    }
    return [];
  }, [poItems, poData]);

  useEffect(() => {
    setSupplierId(poData?.supplier?._id);
    // @ts-expect-error - TS2345: Argument of type 'boolean' is not assignable to parameter of type 'SetStateActio...
    setImperial(poData.supplier?.measurement === 'Imperial');
    setSearch('');
    setUPCQty({});
  }, [poData]);

  useEffect(() => {
    const t = {};
    items.forEach(item => {
      if (!upcQty[item.upc] || !upcQty[item.upc]?.aliases) {
        const tempAttributes = {};
        (item.attributes || []).forEach(attribute => {
          const temp = {
            ...attribute,
            ctn_qty: '',
            qty: 0,
          };
          tempAttributes[attribute.alias] = temp;
        });

        const tempTotal = { ...calculateItem(Object.values(tempAttributes)) };

        const tempPrices = [...(item.prices || [])].sort(
          (a, b) => b.moq - a.moq,
        );
        // @ts-expect-error - TS2339: Property 'current_price' does not exist on type '{ ctn_qty: number; qty: number;...
        tempTotal.current_price =
          tempPrices.find(x => x.moq <= tempTotal.qty)?.price || 0;
        // @ts-expect-error - TS2339: Property 'prices' does not exist on type '{ ctn_qty: number; qty: number; }'.
        tempTotal.prices = [...tempPrices];
        // @ts-expect-error - TS2339: Property 'total' does not exist on type '{ ctn_qty: number; qty: number; }'.
        tempTotal.total = tempTotal.qty * tempTotal.current_price;
        // @ts-expect-error - TS2339: Property 'MOQ' does not exist on type '{ ctn_qty: number; qty: number; }'.
        tempTotal.MOQ = item.MOQ;

        t[item.upc] = {
          aliases: { ...tempAttributes },
          item_upc: item.item_upc,
          prediction: !!item.predictedQty,
          title: item.p_title,
          sku: item.p_sku,
          image: item.p_images?.display_image,
          ...tempTotal,
        };
      }
    });
    setUPCQty({
      ...upcQty,
      ...t,
    });
  }, [items]);

  const selectAllPredicateClick = () => {
    const newData = poUpdateAllPredicate(upcQty, items);
    setUPCQty(newData);
  };

  const clearAllPredicateClick = () => {
    const qtyResult = {};
    items.forEach(item => {
      if ((item.attributes || []).length > 0) {
        const tempAttributes = {};
        item.attributes.forEach(attribute => {
          const temp = {
            ...attribute,
            ctn_qty: '',
            qty: 0,
          };
          tempAttributes[attribute.alias] = temp;
        });
        qtyResult[item.upc] = {
          aliases: { ...tempAttributes },
          prediction: !!item.predictedQty,
          item_upc: item.item_upc,
          prices: [...(item.prices || [])],
        };
      }
    });
    setUPCQty({
      ...qtyResult,
    });
  };

  const columns = useMemo(
    () =>
      createPOItemColumns.map(item => {
        if (item.key === 'predictedQty') {
          const t = { ...item };
          if (items.some(x => x.predictedQty)) {
            // @ts-expect-error - TS18046: 'x' is of type 'unknown'.
            if (Object.values(upcQty).some(x => !x.ctn_qty && x.prediction)) {
              t.component = (
                <PredicateSelectAll
                  selectAllPredicateClick={selectAllPredicateClick}
                />
              );
            } else {
              t.component = (
                <PredicateDeSelectAll
                  selectAllPredicateClick={() => setConfirmationPopup(true)}
                />
              );
            }
          }
          return t;
        }
        return item;
      }),
    [items, upcQty],
  );

  const itemsWithNoPrediction = useMemo(() => {
    let count = 0;
    items.forEach(item => {
      if (!item.predictedQty) count += 1;
    });
    return count;
  }, [items]);

  const searchedItem = useMemo(() => {
    let copy = JSON.parse(JSON.stringify(items));
    if (!showAllItems) {
      copy = copy.filter(item => !!item.predictedQty);
      if (copy.length < 1) {
        copy = JSON.parse(JSON.stringify(items));
      }
    }
    return copy;
  }, [items, showAllItems]);

  const qtyChanged = (item, alias, name, value) => {
    const newData = poQtyChanges(upcQty, item, alias, name, value);
    setUPCQty(newData);
  };

  const predictedQtyClicked = item => {
    if (item.predictedQty && (item.attributes || []).length > 0) {
      const newData = poUpdatePredictedQtyForItem(upcQty, item);
      setUPCQty(newData);
    }
  };

  const update = async () => {
    try {
      if (Object.keys(upcQty).length === 0) {
        return;
      }

      setSaving(true);

      const reqItems = [];
      let hasPriceError = false;
      let hasMOQError = false;
      Object.values(upcQty)
        // @ts-expect-error - TS18046: 'x' is of type 'unknown'.
        .filter(x => x.qty > 0)
        .forEach(item => {
          // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
          if ((item.prices || []).length > 0) {
            // const reqData = { ...item };
            const reqData = {
              // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
              item_upc: item.item_upc,
              // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
              prices: item.prices.sort((a, b) => a.moq - b.moq),
              // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
              regionalized: item.regionalized,
              aliases: [],
            };
            // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
            Object.values(item.aliases)
              // @ts-expect-error - TS18046: 'x' is of type 'unknown'.
              .filter(x => x.ctn_qty)
              .forEach(attribute => {
                if (
                  // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
                  item.MOQ &&
                  // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                  attribute.units_per_carton &&
                  // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                  attribute.ctn_qty !== ''
                ) {
                  if (
                    // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
                    Math.ceil(item.MOQ / attribute.units_per_carton) >
                    // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                    parseInt(attribute.ctn_qty || 0, 10)
                  ) {
                    hasMOQError = true;
                  }
                }

                const temp = {
                  // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                  qty: attribute.qty,
                  // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                  alias: attribute.alias,
                  attributes: {
                    // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                    units_per_carton: attribute.units_per_carton,
                    // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                    carton_length: attribute.carton_length,
                    // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                    carton_width: attribute.carton_width,
                    // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                    carton_height: attribute.carton_height,
                    // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                    carton_net_weight: attribute.carton_net_weight,
                    // @ts-expect-error - TS18046: 'attribute' is of type 'unknown'.
                    carton_gross_weight: attribute.carton_gross_weight,
                  },
                };

                if (imperial) {
                  temp.attributes = [temp.attributes].map(variant =>
                    convertItemToMetric(variant),
                  )[0];
                }
                // @ts-expect-error - TS2345: Argument of type '{ qty: any; alias: any; attributes: { units_per_carton: any; c...
                reqData.aliases.push(temp);
              });

            // @ts-expect-error - TS2345: Argument of type '{ item_upc: any; prices: any; regionalized: any; aliases: neve...
            reqItems.push(reqData);
          } else {
            hasPriceError = true;
          }
        });

      if (hasPriceError) {
        ToastMessage.error(
          'Please set a cost on all items that you intend to include in the PO',
        );
        return;
      }

      if (hasMOQError) {
        ToastMessage.error('Please set quantity more than MOQ');
        return;
      }

      if (reqItems.length === 0) {
        ToastMessage.error('Enter Item Quantity');
        return;
      }

      // @ts-expect-error - TS2339: Property 'aliases' does not exist on type 'never'.
      if (reqItems.length > 0 && !reqItems.some(x => x.aliases.length > 0)) {
        ToastMessage.error('Enter item unit per carton');
        return;
      }

      const promises = [];
      if (reqItems.length > 0) {
        promises.push(
          // @ts-expect-error - TS2345: Argument of type 'Promise<any>' is not assignable to parameter of type 'never'.
          PurchaseOrdersService.addItems(poData?._id, { items: reqItems }),
        );
      }

      await Promise.all(promises);

      ToastMessage.success('Items has been added successfully.');
      closeModal(true);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  const addEditAttribute = (item, alias) => {
    setCurrentItem({ ...item });
    setCurrentAttributeAlias(alias || '');
    setAttributePopup(true);
  };

  const deleteItemAttribute = (item, alias) => {
    const tempItems = deleteAttribute(items, item, alias);
    setData(tempItems);
    if (
      upcQty[item.item_upc]?.aliases &&
      upcQty[item.item_upc].aliases[alias]
    ) {
      let temp = { ...upcQty[item.item_upc] };
      const { [alias]: _, ...aliases } = temp.aliases;

      const tempTotal = { ...calculateItem(Object.values(aliases)) };

      const tempPrices = [...(item.prices || [])].sort((a, b) => b.moq - a.moq);
      // @ts-expect-error - TS2339: Property 'current_price' does not exist on type '{ ctn_qty: number; qty: number;...
      tempTotal.current_price =
        tempPrices.find(x => x.moq <= tempTotal.qty)?.price || 0;
      // @ts-expect-error - TS2339: Property 'total' does not exist on type '{ ctn_qty: number; qty: number; }'.
      tempTotal.total = tempTotal.qty * tempTotal.current_price;
      // @ts-expect-error - TS2339: Property 'prices' does not exist on type '{ ctn_qty: number; qty: number; }'.
      tempTotal.prices = [...tempPrices];
      temp.prediction = !!item.predictedQty;
      temp = {
        ...temp,
        ...tempTotal,
      };
      setUPCQty({
        ...upcQty,
        [item.item_upc]: {
          ...temp,
          aliases,
        },
      });
    }
  };

  const closeAttributeModal = newAttribute => {
    if (newAttribute) {
      let tempItems;
      // @ts-expect-error - TS2339: Property 'attributes' does not exist on type '{}'.
      if (currentItem.attributes.some(x => x.alias === newAttribute.alias)) {
        // @ts-expect-error - TS2345: Argument of type '{}' is not assignable to parameter of type 'IPOProduct'.
        tempItems = updateAttribute(items, currentItem, newAttribute);
      } else {
        // @ts-expect-error - TS2345: Argument of type '{}' is not assignable to parameter of type 'IPOProduct'.
        tempItems = addAttribute(items, currentItem, newAttribute);
      }
      if (
        // @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'.
        upcQty[currentItem.item_upc]?.aliases &&
        // @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'.
        upcQty[currentItem.item_upc].aliases[newAttribute.alias]
      ) {
        qtyChanged(
          // @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'.
          tempItems.find(item => item.item_upc === currentItem.item_upc),
          newAttribute.alias,
          'ctn_qty',
          // @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'.
          upcQty[currentItem.item_upc].aliases[newAttribute.alias].ctn_qty,
        );
      }
      setData(tempItems);
    }
    setAttributePopup(false);
    setCurrentItem({});
    setCurrentAttributeAlias('');
  };

  const editPriceClicked = item => {
    setCurrentItem({ ...item });
    setEditPricePopup(true);
  };

  const closeEditPricePopup = newPrices => {
    if (newPrices) {
      const moq = newPrices[0].moq;
      const tempItems = items.map(item => {
        // @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'.
        if (item.item_upc === currentItem.item_upc) {
          return {
            ...item,
            prices: [...newPrices],
            MOQ: moq,
          };
        }
        return item;
      });
      setData(tempItems);

      /* @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'. */
      const temp = { ...upcQty[currentItem.item_upc] };
      if (!temp.aliases) {
        temp.aliases = {};
      }
      const tempPrices = [...newPrices].sort((a, b) => b.moq - a.moq);

      temp.prices = [...tempPrices];
      temp.current_price =
        tempPrices.find(x => x.moq <= (temp.qty || 0))?.price || 0;
      temp.total = (temp.qty || 0) * temp.current_price;
      temp.MOQ = moq;
      setUPCQty({
        ...upcQty,
        /* @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'. */
        [currentItem.item_upc]: { ...temp },
      });
    }
    // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'SetStateAction<{...
    setCurrentItem(null);
    setEditPricePopup(false);
  };

  const itemSupplierClick = item => {
    window.open(
      `${window.location.pathname}?supplier_id=${item.supplier_id}`,
      '_blank',
    );
  };

  return (
    <>
      {/*
        The timeline is a kit Drawer, a modal overlay of its own. Two open at
        once would each trap focus and hide the other from assistive tech, so
        this modal unmounts while the timeline is open and mounts again when it
        closes; the item state lives here, outside the overlay.
      */}
      <AppModal
        title="Add Items"
        // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
        open={open && !currentTimelineItem?._id}
        size="fullscreen"
        closeModal={() => closeModal()}
      >
        {saving && <LoaderBar content="Saving" />}

        <POTableSearch
          // @ts-expect-error - TS2322: Type 'undefined' is not assignable to type 'boolean'.
          imperial={imperial}
          items={items}
          search={search}
          // @ts-expect-error - TS2322: Type 'Dispatch<SetStateAction<undefined>>' is not assignable to type '(value: bo...
          setImperial={setImperial}
          setItems={setData}
          setSearch={setSearch}
        />
        <AppTable
          RowComponent={
            <MultiAliasItemRow
              // @ts-expect-error - TS2322: Type 'Dispatch<SetStateAction<undefined>>' is not assignable to type '(item: ITi...
              setCurrentItem={setCurrentTimelineItem}
              showTimeline
              upcQty={upcQty}
              qtyChanged={qtyChanged}
              addEditAttribute={addEditAttribute}
              predictedQtyClicked={predictedQtyClicked}
              editPriceClicked={editPriceClicked}
              // @ts-expect-error - TS2322: Type 'Dispatch<SetStateAction<null>>' is not assignable to type '(regionId: stri...
              setRegionId={setRegionId}
              canTakeAction
              itemSupplierClick={itemSupplierClick}
              deleteAttribute={deleteItemAttribute}
              imperial={imperial}
              currency={poData.currency}
            />
          }
          noShadow
          staticHeader
          loading={itemLoading}
          data={searchedItem || []}
          totalData={totalData}
          columns={columns}
          currentPage={filter.page}
          pageSize={filter.limit}
          pageSizeChanged={limit => filterChanged('limit', limit)}
          totalPages={totalPages}
          pageChanged={page => pageChanged(page)}
          keyField="upc"
          footerComponent={
            <div className="sk:flex sk:items-center">
              {itemsWithNoPrediction > 0 &&
                itemsWithNoPrediction !== items?.length && (
                  <Button onPress={() => setShowAllItems(!showAllItems)}>
                    {showAllItems ? (
                      <Minus aria-hidden />
                    ) : (
                      <Plus aria-hidden />
                    )}
                    {showAllItems
                      ? 'Hide In-Stock Items'
                      : `Show all Items Available (${itemsWithNoPrediction})`}
                  </Button>
                )}
            </div>
          }
        />

        <div className="sk:mt-4 sk:flex sk:justify-end sk:gap-2">
          <Button onPress={() => closeModal()}>Cancel</Button>
          <Button
            variant="primary"
            isPending={saving}
            onPress={() => update()}
            isDisabled={items.length === 0}
          >
            Save Changes
          </Button>
        </div>

        {/* @ts-expect-error - TS2740: Type '{ open: boolean; closeModal: (newAttribute: any) => void; attributes: any;... */}
        <AddEditAttributePopup
          open={attributePopup}
          closeModal={closeAttributeModal}
          // @ts-expect-error - TS2339: Property 'currentAttributes' does not exist on type '{}'.
          attributes={currentItem?.currentAttributes || []}
          currentAlias={currentAttributeAlias}
          // @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'.
          itemUPC={currentItem?.item_upc}
          imperial={imperial}
          hideAction
        />
        <DeleteConfirmPopup
          title=""
          content="Are you sure you want to clear all columns?"
          onConfirm={() => clearAllPredicateClick()}
          open={!!confirmationPopup}
          onClose={() => setConfirmationPopup(false)}
        />
        <EditItemPricesPopup
          open={editPricePopup}
          closeModal={closeEditPricePopup}
          // @ts-expect-error - TS2741: Property 'item_upc' is missing in type '{}' but required in type 'IItemForPricin...
          item={currentItem}
          currency={poData.currency}
        />
      </AppModal>
      {/* @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'. */}
      {!!currentTimelineItem?._id && (
        <CondensedTimelinePopup
          refresh={refresh}
          // @ts-expect-error - TS2322: Type '{ refresh: () => void; regionId: null; setCurrentItem: Dispatch<SetStateAc...
          regionId={regionId}
          setCurrentItem={setCurrentTimelineItem}
          // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
          itemId={currentTimelineItem?._id}
          // @ts-expect-error - TS2339: Property 'units_per_carton' does not exist on type 'never'.
          unitsPerCarton={currentTimelineItem?.units_per_carton}
          itemSrc={currentTimelineItem}
          itemTitle={
            // @ts-expect-error - TS2339: Property 'g_attributes' does not exist on type 'never'.
            currentTimelineItem?.g_attributes?.short_title ||
            // @ts-expect-error - TS2339: Property 'p_title' does not exist on type 'never'.
            currentTimelineItem?.p_title
          }
          // @ts-expect-error - TS2339: Property 'p_sku' does not exist on type 'never'.
          itemSKU={currentTimelineItem?.p_sku}
          context="purchase-order"
          inputChanged={data => predictedQtyClicked(data)}
          // @ts-expect-error - TS2339: Property 'predictedQty' does not exist on type 'never'.
          predictedQty={currentTimelineItem?.predictedQty}
          // @ts-expect-error - TS2339: Property 'upc' does not exist on type 'never'.
          itemUPC={currentTimelineItem?.upc}
        />
      )}
    </>
  );
}
